<template>
	<div class="bar-chart-wrapper" :style="{ height: `${height}px` }">
		<Bar :data="chartData" :options="chartOptions" />
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Bar } from "vue-chartjs";
import {
	Chart as ChartJS,
	BarController,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export interface BarDataset {
	label: string;
	data: number[];
	color: string | string[];
}

interface Props {
	labels: string[];
	datasets: BarDataset[];
	height?: number;
	stacked?: boolean;
	horizontal?: boolean;
	hideLegend?: boolean;
	tooltipSuffix?: string;
}

const props = withDefaults(defineProps<Props>(), {
	height: 300,
	stacked: false,
	horizontal: false,
	hideLegend: false,
	tooltipSuffix: "",
});

const chartData = computed(() => ({
	labels: props.labels,
	datasets: props.datasets.map((ds) => ({
		label: ds.label,
		data: ds.data,
		backgroundColor: ds.color,
		borderRadius: 4,
		maxBarThickness: 40,
	})),
}));

const chartOptions = computed(() => ({
	responsive: true,
	maintainAspectRatio: false,
	indexAxis: props.horizontal ? ("y" as const) : ("x" as const),
	scales: {
		x: {
			stacked: props.stacked,
			grid: { display: false },
		},
		y: {
			stacked: props.stacked,
			beginAtZero: true,
			ticks: { stepSize: 1 },
		},
	},
	plugins: {
		legend: props.hideLegend
			? { display: false }
			: {
					position: "bottom" as const,
					labels: {
						padding: 16,
						usePointStyle: true,
						pointStyleWidth: 10,
						font: { size: 12 },
					},
				},
		tooltip: {
			mode: "index" as const,
			intersect: false,
			...(props.tooltipSuffix
				? {
						callbacks: {
							label: (ctx: any) =>
								`${ctx.dataset.label}: ${ctx.parsed.x ?? ctx.parsed.y}${props.tooltipSuffix}`,
						},
					}
				: {}),
		},
	},
}));
</script>

<style scoped>
.bar-chart-wrapper {
	position: relative;
}
</style>
